import { Injectable, inject } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpContextToken,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { MsalService } from '@azure/msal-angular';
import { AppDialogService } from '@shared/dialogs/app-dialog.service';
import { apiScope } from '../../auth/msal.config';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../services/AuthService';

interface ErrorResponse {
  error?: string;
  message?: string;
  title?: string;
  detail?: string;
  reason?: string;
  expiresAt?: string;
}

const AUTH_RETRY_CONTEXT = new HttpContextToken<boolean>(() => false);

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private readonly router = inject(Router);
  private readonly appDialog = inject(AppDialogService);
  private readonly msalService = inject(MsalService);
  private readonly authService = inject(AuthService);
  private lastAlertMessage = '';
  private lastAlertAt = 0;
  private suspensionDialogOpen = false;

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        const payload = this.toPayload(error.error);
        const code = this.extractCode(error.error);

        if (error.status === 403 && code === 'TERMS_NOT_ACCEPTED') {
          return throwError(() => error);
        }

        if (this.isAccountSuspension(error, payload)) {
          void this.handleAccountSuspension();
          return throwError(() => error);
        }

        if (error.status === 403) {
          if (payload.expiresAt) {
            const suspendedUntil = new Date(payload.expiresAt);
            const formatted = Number.isNaN(suspendedUntil.getTime())
              ? payload.expiresAt
              : suspendedUntil.toLocaleDateString();
            this.notifyUser(
              `Your account is suspended until ${formatted}. ${payload.reason ?? ''}`.trim()
            );
          } else {
            this.notifyUser(payload.message ?? payload.error ?? 'Access denied.');
          }
        }

        if (error.status === 401) {
          return this.retryWithFreshToken(req, next, error);
        }

        return throwError(() => error);
      })
    );
  }

  private retryWithFreshToken(
    req: HttpRequest<unknown>,
    next: HttpHandler,
    error: HttpErrorResponse
  ): Observable<HttpEvent<unknown>> {
    if (!this.isApiRequest(req.url) || req.context.get(AUTH_RETRY_CONTEXT)) {
      return this.redirectToLogin(error);
    }

    const account =
      this.msalService.instance.getActiveAccount()
      ?? this.msalService.instance.getAllAccounts()[0]
      ?? null;

    if (!account) {
      return this.redirectToLogin(error);
    }

    return this.msalService.acquireTokenSilent({
      account,
      scopes: [apiScope],
    }).pipe(
      switchMap((result) => {
        if (!result.accessToken) {
          return this.redirectToLogin(error);
        }

        const retryRequest = req.clone({
          context: req.context.set(AUTH_RETRY_CONTEXT, true),
          setHeaders: {
            Authorization: `Bearer ${result.accessToken}`,
          },
        });

        return next.handle(retryRequest).pipe(
          catchError((retryError) => this.redirectToLogin(retryError))
        );
      }),
      catchError(() => this.redirectToLogin(error))
    );
  }

  private isApiRequest(url: string): boolean {
    return url.startsWith(environment.apiUrl);
  }

  private redirectToLogin(error: HttpErrorResponse): Observable<never> {
    this.notifyUser('Session expired. Please login again.');
    if (!this.router.url.startsWith('/login')) {
      void this.router.navigate(['/login'], {
        queryParams: { returnUrl: this.router.url || '/home' },
      });
    }
    return throwError(() => error);
  }

  private toPayload(payload: unknown): ErrorResponse {
    if (!payload || typeof payload !== 'object') return {};
    return payload as ErrorResponse;
  }

  private extractCode(payload: unknown): string | undefined {
    if (!payload || typeof payload !== 'object') return undefined;

    const direct = (payload as { code?: unknown }).code;
    if (typeof direct === 'string') return direct;

    const nested = (payload as { error?: { code?: unknown } }).error?.code;
    if (typeof nested === 'string') return nested;

    return undefined;
  }

  private isAccountSuspension(error: HttpErrorResponse, payload: ErrorResponse): boolean {
    if (error.status !== 403) return false;

    const title = payload.title?.trim().toLowerCase();
    const detail = payload.detail?.trim().toLowerCase();
    const message = payload.message?.trim().toLowerCase();

    return title === 'account suspended'
      || detail?.includes('your account has been suspended') === true
      || message?.includes('account suspended') === true;
  }

  private async handleAccountSuspension(): Promise<void> {
    if (this.suspensionDialogOpen) {
      return;
    }

    this.suspensionDialogOpen = true;

    try {
      await this.appDialog.alert({
        title: 'Account suspended',
        message: 'Your account is suspended. Contact grievance@peerliv.in for support.',
        confirmLabel: 'OK',
        tone: 'danger',
        disableClose: true,
      });
    } finally {
      this.authService.logout();
    }
  }

  private notifyUser(message: string): void {
    const now = Date.now();
    const normalized = message.trim();
    if (!normalized) return;

    if (this.lastAlertMessage === normalized && now - this.lastAlertAt < 3000) {
      return;
    }

    this.lastAlertMessage = normalized;
    this.lastAlertAt = now;
    void this.appDialog.alert({
      title: 'Request error',
      message: normalized,
      tone: 'danger',
    });
  }
}
